import {useEffect, useRef, useState} from 'react';
import {
    Box,
    Collapse,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    ChevronRight as ChevronRightIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

/**
 * Section item for the navigator
 */
export interface NavigatorSection {
    id: string;
    displayNum: string;     // "§ 1-1" or similar
    title: string;          // Section title/heading
    chapterNum: number | null;  // For grouping
    hasChanges?: boolean;   // Unsaved changes indicator (edit mode)
    secondary?: string;     // Secondary text (e.g., page range)
}

/**
 * Chapter group for hierarchical display
 */
export interface ChapterGroup {
    chapterNum: number | null;
    label: string;          // "Kapittel 1" or "Annet"
    sections: NavigatorSection[];
}

export interface SectionNavigatorProps {
    /** Title shown in header */
    title?: string;
    /** Grouped sections to display */
    groups: ChapterGroup[];
    /** Currently selected section ID */
    selectedId: string | null;
    /** Called when a section is clicked */
    onSelect: (id: string) => void;
    /** Whether the panel can be collapsed */
    collapsible?: boolean;
    /** Initial expanded state */
    defaultExpanded?: boolean;
    /** Show add button (edit mode) */
    showAddButton?: boolean;
    /** Called when add button is clicked */
    onAdd?: () => void;
    /** Loading state for add button */
    addLoading?: boolean;
    /** Custom width when expanded */
    expandedWidth?: number | string;
    /** Custom width when collapsed */
    collapsedWidth?: number;
}

/**
 * Shared section navigation sidebar.
 * Used in both DocsPage and PendingDocDetailPage for consistent navigation.
 */
export function SectionNavigator({
    title = 'SECTIONS',
    groups,
    selectedId,
    onSelect,
    collapsible = true,
    defaultExpanded = true,
    showAddButton = false,
    onAdd,
    addLoading = false,
    expandedWidth = 280,
    collapsedWidth = 48,
}: SectionNavigatorProps) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<number | null>>(new Set());
    const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());
    const hasScrolledRef = useRef<string | null>(null);

    const totalCount = groups.reduce((sum, g) => sum + g.sections.length, 0);

    const toggleGroup = (chapterNum: number | null) => {
        setCollapsedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(chapterNum)) {
                newSet.delete(chapterNum);
            } else {
                newSet.add(chapterNum);
            }
            return newSet;
        });
    };

    // Find which chapter contains the selected section
    const findChapterForSection = (sectionId: string | null): number | null | undefined => {
        if (!sectionId) return undefined;
        for (const group of groups) {
            if (group.sections.some(s => s.id === sectionId)) {
                return group.chapterNum;
            }
        }
        return undefined;
    };

    // Auto-expand chapter and scroll to selected section when selectedId changes
    useEffect(() => {
        if (!selectedId || !expanded) return;

        // Only scroll once per selectedId to avoid constant scrolling
        if (hasScrolledRef.current === selectedId) return;

        // Find and expand the chapter containing the selected section
        const chapterNum = findChapterForSection(selectedId);
        if (chapterNum !== undefined && collapsedGroups.has(chapterNum)) {
            setCollapsedGroups(prev => {
                const newSet = new Set(prev);
                newSet.delete(chapterNum);
                return newSet;
            });
        }

        // Scroll to the selected section after a short delay (for collapse animation)
        const timeoutId = setTimeout(() => {
            const element = sectionRefs.current.get(selectedId);
            if (element) {
                const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                element.scrollIntoView({behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'center'});
                hasScrolledRef.current = selectedId;
            }
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [selectedId, expanded, groups]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box sx={{
            width: expanded ? expandedWidth : collapsedWidth,
            minWidth: expanded ? expandedWidth : collapsedWidth,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'width, min-width',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <Box sx={{
                p: expanded ? 2 : 1,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: expanded ? 'space-between' : 'center',
                alignItems: 'center',
            }}>
                {expanded ? (
                    <>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            {collapsible && (
                                <IconButton
                                    size="small"
                                    onClick={() => setExpanded(false)}
                                    aria-label="Collapse sections panel"
                                >
                                    <ExpandLessIcon fontSize="small" sx={{transform: 'rotate(-90deg)'}}/>
                                </IconButton>
                            )}
                            <Typography variant="subtitle2" sx={{
                                color: "text.secondary"
                            }}>
                                {title} ({totalCount})
                            </Typography>
                        </Box>
                        {showAddButton && onAdd && (
                            <Tooltip title="Add section">
                                <IconButton
                                    size="small"
                                    onClick={onAdd}
                                    disabled={addLoading}
                                    aria-label="Add section"
                                >
                                    <AddIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        )}
                    </>
                ) : (
                    <Tooltip title="Expand sections panel">
                        <IconButton
                            size="small"
                            onClick={() => setExpanded(true)}
                            aria-label="Expand sections panel"
                        >
                            <ChevronRightIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            {/* Section list */}
            {expanded && (
                <List sx={{flex: 1, overflow: 'auto', py: 0}}>
                    {groups.length > 0 ? (
                        groups.map((group) => {
                            // Only show chapter headers when grouping is meaningful
                            // (at least one chapter has >1 section, indicating real structure)
                            const showChapterHeaders = groups.length > 1 && groups.some(g => g.sections.length > 1);
                            return (
                                <Box key={group.chapterNum ?? 'other'}>
                                    {/* Chapter header - only show if grouping is meaningful */}
                                    {showChapterHeaders && (
                                        <ListItemButton
                                            onClick={() => toggleGroup(group.chapterNum)}
                                            aria-expanded={!collapsedGroups.has(group.chapterNum)}
                                            sx={{
                                                py: 0.75,
                                                bgcolor: 'action.hover',
                                                '&:hover': {bgcolor: 'action.selected'},
                                            }}
                                        >
                                            {collapsedGroups.has(group.chapterNum) ? (
                                                <ExpandMoreIcon fontSize="small" aria-hidden="true" sx={{mr: 0.5}}/>
                                            ) : (
                                                <ExpandLessIcon fontSize="small" aria-hidden="true" sx={{mr: 0.5}}/>
                                            )}
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: "bold",
                                                    color: "text.secondary"
                                                }}>
                                                {group.label} ({group.sections.length})
                                            </Typography>
                                        </ListItemButton>
                                    )}
                                    {/* Sections within group */}
                                    <Collapse in={!collapsedGroups.has(group.chapterNum)}>
                                        {group.sections.map((section) => (
                                            <ListItem
                                                key={section.id}
                                                disablePadding
                                                ref={(el) => {
                                                    if (el) sectionRefs.current.set(section.id, el);
                                                    else sectionRefs.current.delete(section.id);
                                                }}
                                            >
                                                <ListItemButton
                                                    selected={selectedId === section.id}
                                                    onClick={() => onSelect(section.id)}
                                                    sx={{
                                                        py: 1,
                                                        pl: groups.length > 1 ? 3 : 2,
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                                {section.hasChanges && (
                                                                    <Typography
                                                                        component="span"
                                                                        aria-label="Unsaved changes"
                                                                        sx={{
                                                                            color: "warning.main",
                                                                            fontWeight: 'bold'
                                                                        }}>
                                                                        •
                                                                    </Typography>
                                                                )}
                                                                {section.displayNum && (
                                                                    <Typography
                                                                        component="span"
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: "primary.main",
                                                                            fontWeight: 'medium',
                                                                            whiteSpace: 'nowrap'
                                                                        }}>
                                                                        {section.displayNum}
                                                                    </Typography>
                                                                )}
                                                                <Typography
                                                                    variant="body2"
                                                                    noWrap
                                                                    sx={{
                                                                        fontWeight: section.hasChanges ? 'bold' : 'normal',
                                                                    }}
                                                                >
                                                                    {section.title}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        secondary={section.secondary}
                                                        slotProps={{
                                                            secondary: {variant: 'caption'}
                                                        }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </Collapse>
                                </Box>
                            );})
                    ) : (
                        <Box sx={{p: 2, textAlign: 'center'}}>
                            <Typography variant="body2" sx={{
                                color: "text.secondary"
                            }}>
                                No sections
                            </Typography>
                        </Box>
                    )}
                </List>
            )}
        </Box>
    );
}

/**
 * Helper to group sections by chapter number
 */
export function groupSectionsByChapter(
    sections: NavigatorSection[],
    getLabel: (chapterNum: number | null) => string = (n) => n !== null ? `Kapittel ${n}` : 'Annet'
): ChapterGroup[] {
    const groups = new Map<number | null, ChapterGroup>();

    sections.forEach(section => {
        const key = section.chapterNum;
        if (!groups.has(key)) {
            groups.set(key, {
                chapterNum: key,
                label: getLabel(key),
                sections: [],
            });
        }
        groups.get(key)!.sections.push(section);
    });

    // Sort groups by chapter number (null goes to end)
    const sorted = Array.from(groups.values()).sort((a, b) => {
        if (a.chapterNum === null && b.chapterNum === null) return 0;
        if (a.chapterNum === null) return 1;
        if (b.chapterNum === null) return -1;
        return a.chapterNum - b.chapterNum;
    });

    return sorted;
}

export default SectionNavigator;
